import { proxy } from 'valtio';
import { MapStyle } from '~/common/types';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import { commentBboxPadding, commentExtraSpacing, commentLineWidth, commentRadius, getCircleBgColor } from './common';

export const editCommentObject = proxy({
  commentObject: { isDragging: false },
});

export class EditCommentObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  private point: Point;
  private isDragging = false;
  private onCommentUpdated: (p: LngLat) => void;

  constructor(id: string, surface: SurfaceBase, onCommentUpdated: (p: LngLat) => void) {
    super(id, surface);
    const comment = this.getCurrentCommentObject();
    if (!comment) throw new Error('currentObject is not a comment!');

    this.point = this.surface.toScreenCoords({
      lng: comment.geometry.coordinates[0],
      lat: comment.geometry.coordinates[1],
    });

    this.onCommentUpdated = onCommentUpdated;
    this.interactive = true;

    this.on('dragStart', () => {
      this.isDragging = true;
      this.renderAll();
      editCommentObject.commentObject.isDragging = true;
    });
    this.on('dragging', (e) => {
      this.point = {
        x: e.point.x - commentExtraSpacing,
        y: e.point.y - commentExtraSpacing,
      };
      this.renderAll();
    });
    this.on('dragEnd', () => {
      this.isDragging = false;
      this.onCommentUpdated(this.surface.toMapCoords(this.point));
      this.renderAll();
      editCommentObject.commentObject.isDragging = false;
    });
  }

  getCurrentCommentObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'comment') return;
    return currentObject;
  }

  refreshScreenCoords(): void {
    const comment = this.getCurrentCommentObject();
    if (!comment) return;
    this.point = this.surface.toScreenCoords({
      lng: comment.geometry.coordinates[0],
      lat: comment.geometry.coordinates[1],
    });
  }

  render(ctx: CanvasRenderingContext2D) {
    const comment = this.getCurrentCommentObject();
    if (!comment) return;
    const { createdBy } = comment;
    const markerColor = getCircleBgColor({
      initial: createdBy.name.charAt(0).toUpperCase(),
    });

    ctx.globalAlpha = 1;

    if (this.isDragging) {
      ctx.globalAlpha = opacityWhileDragging;
    }

    // marker shell
    ctx.beginPath();
    ctx.strokeStyle = markerColor;
    ctx.lineWidth = commentLineWidth;
    ctx.fillStyle = markerColor;
    ctx.moveTo(this.point.x, this.point.y);
    ctx.lineTo(this.point.x, this.point.y + commentExtraSpacing);
    const radius = commentExtraSpacing; // Arc radius
    const startAngle = -3.4; // Starting point on circle
    const endAngle = Math.PI + Math.PI / 2; // End point on circle
    const counterclockwise = true; // Draw counterclockwise
    ctx.arc(
      this.point.x + commentExtraSpacing,
      this.point.y + commentExtraSpacing,
      radius,
      startAngle,
      endAngle,
      counterclockwise,
    );
    ctx.fill();
    ctx.closePath();
    ctx.stroke();

    // circle
    ctx.beginPath();
    ctx.strokeStyle = markerColor;
    ctx.lineWidth = commentLineWidth;
    ctx.fillStyle = markerColor;
    ctx.arc(this.point.x + commentExtraSpacing, this.point.y + commentExtraSpacing, commentRadius, 0, 2 * Math.PI);
    ctx.fill();

    // text
    ctx.font = 'bold 14pt Inter';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      createdBy.name ? createdBy.name.charAt(0).toUpperCase() : '',
      this.point.x + commentExtraSpacing,
      this.point.y + commentExtraSpacing,
    );
    ctx.closePath();
    ctx.stroke();

    // save some compute by not rendering text when dragging
    if (!this.isDragging) {
      const store = this.getStore();
      if (
        !store.currentMap.isLoading &&
        (store.currentMap.baseMap === MapStyle.satellite || store.currentMap.baseMap === MapStyle.dark)
      ) {
        ctx.strokeStyle = 'black';
        ctx.fillStyle = 'white';
      } else {
        ctx.strokeStyle = 'white';
        ctx.fillStyle = 'black';
      }

      ctx.font = 'bold 1.2rem sans-serif';
      ctx.textBaseline = 'middle';
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    this.boundingBox(ctx, color, true);
  }

  boundingBox(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    color: string,
    isHitArea: boolean = false,
  ) {
    ctx.beginPath();
    const x = this.point.x - commentBboxPadding;
    const y = this.point.y - commentBboxPadding;
    ctx.strokeStyle = color;
    ctx.lineWidth = commentLineWidth;
    ctx.rect(x, y, commentBboxPadding * 2, commentBboxPadding * 2);

    ctx.stroke();

    if (isHitArea) {
      ctx.fillStyle = color;
      ctx.fillRect(x, y, commentBboxPadding * 2, commentBboxPadding * 2);
    }
  }
}
